import { useState, useEffect } from 'react';
import { Box, Paper, Typography, Tooltip, Button, CircularProgress, Alert } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Download as DownloadIcon } from '@mui/icons-material';
import { UploadedFilesModal } from './UploadedFilesModal';
import { formatUTCForDisplay } from '../../utils/dateUtils';
import { XlsService, VesselUploadGroup } from '../../services/xlsService';

interface SelectedVessel {
  id: string;
  vesselName: string;
  vesselImo: string;
}

export const VesselVoyageXlsUploadPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVessel, setSelectedVessel] = useState<SelectedVessel | null>(null);
  const [vesselUploads, setVesselUploads] = useState<VesselUploadGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchVesselUploads();
  }, []);

  const fetchVesselUploads = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await XlsService.getVesselUploadsList();
      setVesselUploads(data);
    } catch (err) {
      console.error('Failed to fetch vessel uploads:', err);
      setError('Failed to load vessel uploads. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (vessel: SelectedVessel) => {
    setSelectedVessel(vessel);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedVessel(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadLoading(true);
      setError(null);

      const { blob, fileName } = await XlsService.downloadAdminTemplate();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'voyage-template-admin.xlsx'; // Use server filename or fallback
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download template:', err);
      setError('Failed to download template. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'vesselName',
      headerName: 'Vessel Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'vesselImo',
      headerName: 'Vessel IMO',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      flex: 1,
      minWidth: 180,
      valueGetter: (_value, row) => {
        const vessel = vesselUploads.find((v) => v.vesselId === row.id);
        const uploads = vessel?.uploadedFileInfo || [];

        // Filter only raw uploads (uploadedType = 1)
        const rawUploads = uploads.filter((u) => u.uploadedType === 1);
        if (rawUploads.length === 0) return '-';

        // Get the latest upload by created date
        const latestUpload = rawUploads.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0];

        return latestUpload.createdAt || '-';
      },
      renderCell: (params) => {
        const value = params.value as string | undefined;
        const display = value && value !== '-' ? formatUTCForDisplay(value, false) : '-';
        return <Typography variant="body2">{display}</Typography>;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      flex: 1,
      minWidth: 180,
      valueGetter: (_value, row) => {
        const vessel = vesselUploads.find((v) => v.vesselId === row.id);
        const uploads = vessel?.uploadedFileInfo || [];

        // Filter only raw uploads (uploadedType = 1)
        const rawUploads = uploads.filter((u) => u.uploadedType === 1);
        if (rawUploads.length === 0) return '-';

        // Get the latest upload by updated date
        const latestUpload = rawUploads.sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )[0];

        return latestUpload.updatedAt || '-';
      },
      renderCell: (params) => {
        const value = params.value as string | undefined;
        const display = value && value !== '-' ? formatUTCForDisplay(value, false) : '-';
        return <Typography variant="body2">{display}</Typography>;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      minWidth: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View uploaded files">
            <Button
              variant="outlined"
              size="small"
              sx={{
                minHeight: 28,
                padding: '2px 8px',
                textTransform: 'none',
              }}
              onClick={() => {
                handleOpenModal({
                  id: params.row.id,
                  vesselName: params.row.vesselName,
                  vesselImo: params.row.vesselImo,
                });
              }}
            >
              View Uploaded Files
            </Button>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Transform API data to DataGrid rows
  const rows = vesselUploads.map((vessel) => ({
    id: vessel.vesselId,
    vesselName: vessel.vesselName,
    vesselImo: vessel.vesselImo,
  }));

  return (
    <Box sx={{ p: 0 }}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Upload voyage spreadsheet</Typography>
          <Tooltip title="Download standardized excel empty template">
            <span>
              <Button
                variant="outlined"
                startIcon={downloadLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleDownloadTemplate}
                disabled={downloadLoading}
                sx={{
                  textTransform: 'none',
                }}
              >
                {downloadLoading ? 'Downloading...' : 'Empty Template'}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Box>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 400,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            sx={{
              border: 0,
              '& .MuiDataGrid-cell': {
                padding: '8px',
              },
            }}
          />
        </Paper>
      )}

      {/* Uploaded Files Modal */}
      {selectedVessel && (
        <UploadedFilesModal
          open={modalOpen}
          onClose={handleCloseModal}
          vesselName={selectedVessel.vesselName}
          vesselImo={selectedVessel.vesselImo}
          vesselId={selectedVessel.id}
        />
      )}
    </Box>
  );
};
